import {
  deleteV1CompaniesCompanyIdManagedCompaniesId,
  getV1CompaniesCompanyIdAccountsConnected,
  getV1CompaniesCompanyIdManagedCompanies,
  getV1CompaniesCompanyIdManagedCompaniesId,
  putV1CompaniesCompanyIdManagedCompaniesId,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useState } from 'react';

import { MAX_API_PAGE_SIZE } from '~constants/consts';
import connection from '~services/connectionModule';
import { extractPagination, Pagination } from '~services/pagination';

import { Account } from '../useAccount/models';
import { Company } from '../useCompany/models';

type CreateManagedCompanyParams = {
  companyId: string;
  managedCompanyId: string;
};

type DeleteManagedCompanyParams = {
  companyId: string;
  managedCompanyId: string;
};

export type GetConnectedAccountsByCompanyIdParams = {
  companyId: string;
  cursor?: string;
  query?: string;
};

type GetManagedCompaniesByCompanyIdParams = {
  companyId: string;
  pagination?: Pagination;
};

type GetManagedCompanyByIdParams = {
  companyId: string;
  managedCompanyId: string;
};

export default function useManagedCompanies() {
  const [isLoading, setIsLoading] = useState(false);

  async function createManagedCompany(params: CreateManagedCompanyParams) {
    try {
      const response = await putV1CompaniesCompanyIdManagedCompaniesId({
        path: { 'company-id': params.companyId, id: params.managedCompanyId },
      });

      return Company.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.managed_companies.failed_to_create_managed_account'),
      );

      return null;
    }
  }

  async function deleteManagedCompany(params: DeleteManagedCompanyParams) {
    try {
      await deleteV1CompaniesCompanyIdManagedCompaniesId({
        path: { 'company-id': params.companyId, id: params.managedCompanyId },
      });

      return null;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.managed_companies.failed_to_create_managed_account'),
      );

      return null;
    }
  }

  async function getConnectedAccountsByCompanyId(
    params: GetConnectedAccountsByCompanyIdParams,
  ) {
    try {
      const response = await getV1CompaniesCompanyIdAccountsConnected({
        path: { 'company-id': params.companyId },
        query: {
          'page[after]': params.cursor || undefined,
          'page[limit]': MAX_API_PAGE_SIZE,
          'search[datagrid]': params.query || undefined,
        },
      });
      const accounts = response.data.data.map((account) => Account.parse(account));
      const pagination = extractPagination(response);

      return { data: accounts, pagination };
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.managed_companies.failed_to_fetch_connected_accounts'),
      );

      return null;
    }
  }

  async function getManagedCompaniesByCompanyId(
    params: GetManagedCompaniesByCompanyIdParams,
  ) {
    try {
      const response = await getV1CompaniesCompanyIdManagedCompanies({
        path: { 'company-id': params.companyId },
        query: {
          'page[limit]': MAX_API_PAGE_SIZE,
          'page[after]': params.pagination?.after,
          'page[before]': params.pagination?.before,
        },
      });
      const companies = response.data.data.map((company) => Company.parse(company));
      const pagination = extractPagination(response);

      return { data: companies, pagination };
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.managed_companies.failed_to_fetch_managed_companies'),
      );

      return null;
    }
  }

  async function getManagedCompanyById(params: GetManagedCompanyByIdParams) {
    setIsLoading(true);
    try {
      const response = await getV1CompaniesCompanyIdManagedCompaniesId({
        path: { 'company-id': params.companyId, id: params.managedCompanyId },
      });

      return Company.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.managed_companies.failed_to_fetch_managed_company'),
      );

      return null;
    } finally {
      setIsLoading(false);
    }
  }

  return {
    createManagedCompany,
    deleteManagedCompany,
    getConnectedAccountsByCompanyId,
    getManagedCompanyById,
    getManagedCompaniesByCompanyId,
    isLoading,
  };
}
